import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { responsesFuncMap } from "../../../services/storedResponseServices";
import NoDataComponent from "../../NoDataComponent";
import DynamicTable from "../../DynamicTable";
import ComponentLoader from "../../ComponentLoader";

import styles from "../UserTabDetail/index.module.scss";

const transformHeadersMap: Partial<
  Record<keyof typeof responsesFuncMap, Record<string, string>>
> = {
  CRS: { tin: "TIN", country: "Country", noTinIssuedReason: "No TIN Reason" },
};

type StoredResponseTableName = keyof typeof responsesFuncMap;

interface StoredResponseTabProps {
  tableName: StoredResponseTableName;
}

export default function StoredResponseTab({
  tableName,
}: StoredResponseTabProps) {
  const { userId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<Record<string, unknown>[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadRecords = async () => {
      setIsLoading(true);
      setRecords([]);

      try {
        const result = await responsesFuncMap[tableName](userId || "");
        if (!cancelled) {
          setRecords(result);
        }
      } catch {
        if (!cancelled) {
          setRecords([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadRecords();

    return () => {
      cancelled = true;
    };
  }, [tableName, userId]);

  const mappedHeaders = transformHeadersMap[tableName];

  const transformedData = useMemo(() => {
    if (!mappedHeaders) return records;

    return records.map((record) =>
      Object.assign(
        {},
        ...Object.keys(mappedHeaders).map((header) => ({
          [mappedHeaders[header]]: record[header],
        }))
      )
    );
  }, [mappedHeaders, records]);

  const transformedHeaders = useMemo(() => {
    if (!records[0]) return [];

    return mappedHeaders
      ? Object.values(mappedHeaders)
      : Object.keys(records[0]);
  }, [mappedHeaders, records]);

  return (
    <div className={records.length === 0 ? styles.tableLoader : ""}>
      <div className={styles.loader}>
        {isLoading && <ComponentLoader isLoading={isLoading} />}
      </div>
      {records.length === 0 && !isLoading && <NoDataComponent />}
      {transformedData.length !== 0 && !isLoading && (
        <div className={styles.dynamicTable}>
          <div className={styles.listGroupTitle}>Stored Response</div>
          <DynamicTable
            data={transformedData}
            selectedTable={tableName}
            headers={transformedHeaders}
          />
        </div>
      )}
    </div>
  );
}
